import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import {
  EmailTemplateChannel,
  EmailTemplateFactory,
  EmailTemplatePayload,
  isChannelEnabled,
} from '@/lib/admin/email-templates/models/email-template.model';
import {
  EMAIL_RECIPIENT_TOKENS,
  NOTIFICATION_RECIPIENT_TOKENS,
} from '@/lib/admin/email-templates/constants/recipient-tokens';
import { AvailableVariablesPanel } from '@/lib/admin/email-templates/components/AvailableVariablesPanel';
import { useVariableInsertion } from '@/lib/admin/email-templates/hooks/useVariableInsertion';

export type EmailTemplateModalMode = 'create' | 'edit';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: EmailTemplateModalMode;
  /** Canale del tab attivo: decide quali campi sono mostrati. */
  channel: EmailTemplateChannel;
  /** Valori iniziali: record esistente (edit), copia (duplica) o record vuoto (create). */
  initialValues: EmailTemplatePayload;
  /** Chiavi già esistenti, per la validazione di unicità lato client. */
  existingKeys: string[];
  onSubmit: (payload: EmailTemplatePayload) => Promise<void>;
  isSaving: boolean;
}

const TEMPLATE_KEY_PATTERN = /^[a-z0-9_]+$/;

/**
 * Modale di creazione/modifica di un template.
 *
 * Mostra solo i campi del canale attivo (email o notifica): lo stesso record
 * pilota entrambi i canali, ma mescolarli in un'unica form renderebbe opaco
 * quale coppia campo/destinatari governa cosa. In creazione il canale è
 * scegliibile qui dentro (toggle), perché il tab da cui si è partiti è solo un
 * punto di ingresso.
 *
 * `subject` è NOT NULL a DB: in creazione resta quindi visibile anche sul canale
 * notifica (unico campo "fuori canale"), altrimenti l'insert fallirebbe.
 */
export const EmailTemplateModal = ({
  isOpen,
  onClose,
  mode,
  channel,
  initialValues,
  existingKeys,
  onSubmit,
  isSaving,
}: EmailTemplateModalProps) => {
  const { t } = useI18n();
  const { registerTarget, insertAtCursor } = useVariableInsertion();

  const [values, setValues] = useState<EmailTemplatePayload>(initialValues);
  const [activeChannel, setActiveChannel] = useState<EmailTemplateChannel>(channel);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // La modale è montata di continuo dal parent: il reset dei valori avviene
  // all'apertura (e a ogni cambio di record/canale iniziale), non allo smontaggio.
  useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues);
    setActiveChannel(channel);
    setErrors({});
  }, [isOpen, initialValues, channel]);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [values, initialValues]
  );

  const setField = <K extends keyof EmailTemplatePayload>(
    field: K,
    value: EmailTemplatePayload[K]
  ) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field as string]) return previous;
      const next = { ...previous };
      delete next[field as string];
      return next;
    });
  };

  /** Campi testo nullable: la stringa vuota va salvata come NULL, non come ''. */
  const setNullableField = (
    field: 'description' | 'mailgun_draft_link' | 'redirect_url' | 'notification_message',
    value: string
  ) => setField(field, value.length > 0 ? value : null);

  const recipientOptions = useMemo(
    () =>
      (activeChannel === 'email'
        ? EMAIL_RECIPIENT_TOKENS
        : NOTIFICATION_RECIPIENT_TOKENS
      ).map((token) => ({
        value: token,
        label: t(`emailTemplates.recipientTokens.${token}` as 'emailTemplates.recipientTokens.admin'),
      })),
    [t, activeChannel]
  );

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const key = values.template_key.trim();

    if (!key) {
      nextErrors.template_key = t('emailTemplates.errors.keyRequired');
    } else if (!TEMPLATE_KEY_PATTERN.test(key)) {
      nextErrors.template_key = t('emailTemplates.errors.keyFormat');
    } else if (mode === 'create' && existingKeys.includes(key)) {
      nextErrors.template_key = t('emailTemplates.errors.duplicateKey');
    }

    if (!values.subject.trim()) {
      nextErrors.subject = t('emailTemplates.errors.subjectRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    await onSubmit({ ...values, template_key: values.template_key.trim() });
  };

  const handleRequestClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const isEmailChannel = activeChannel === 'email';
  // In creazione sul canale notifica il subject resta comunque richiesto dal DB.
  const showSubject = isEmailChannel || mode === 'create';
  const channelEnabled = isChannelEnabled(values, activeChannel);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleRequestClose}
        size="xl"
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              {mode === 'create'
                ? t('emailTemplates.modal.createTitle')
                : t('emailTemplates.modal.editTitle')}
            </span>
            {mode === 'edit' && (
              <span className="font-mono text-sm text-gray-500">{initialValues.template_key}</span>
            )}
            {isDirty && (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                {t('emailTemplates.modal.unsavedChanges')}
              </Badge>
            )}
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={handleRequestClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || (!isDirty && mode === 'edit')}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {/* Colonna campi */}
          <div className="flex flex-col gap-4">
            {mode === 'create' && (
              <div className="flex flex-col gap-1.5">
                <Label>{t('emailTemplates.modal.channelLabel')}</Label>
                <div className="flex gap-2">
                  {(['email', 'notification'] as EmailTemplateChannel[]).map((option) => (
                    <Button
                      key={option}
                      type="button"
                      variant={activeChannel === option ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActiveChannel(option)}
                    >
                      {option === 'email'
                        ? t('emailTemplates.tabs.emails')
                        : t('emailTemplates.tabs.notifications')}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  {t('emailTemplates.modal.channelHint')}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="template_key">{t('emailTemplates.fields.templateKey')}</Label>
              <Input
                id="template_key"
                value={values.template_key}
                onChange={(event) => setField('template_key', event.target.value)}
                readOnly={mode === 'edit'}
                disabled={mode === 'edit'}
                placeholder={t('emailTemplates.fields.templateKeyPlaceholder')}
                className={cn('font-mono', errors.template_key && 'border-red-500')}
              />
              {errors.template_key ? (
                <p className="text-xs text-red-600">{errors.template_key}</p>
              ) : (
                <p className="text-xs text-gray-500">
                  {mode === 'edit'
                    ? t('emailTemplates.fields.templateKeyReadonlyHint')
                    : t('emailTemplates.fields.templateKeyHint')}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="description">{t('emailTemplates.fields.description')}</Label>
              <Input
                id="description"
                value={values.description ?? ''}
                onChange={(event) => setNullableField('description', event.target.value)}
                placeholder={t('emailTemplates.fields.descriptionPlaceholder')}
                {...registerTarget('description', (value) =>
                  setNullableField('description', value)
                )}
              />
            </div>

            {showSubject && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="subject">{t('emailTemplates.fields.subject')}</Label>
                <Input
                  id="subject"
                  value={values.subject}
                  onChange={(event) => setField('subject', event.target.value)}
                  placeholder={t('emailTemplates.fields.subjectPlaceholder')}
                  className={cn(errors.subject && 'border-red-500')}
                  {...registerTarget('subject', (value) => setField('subject', value))}
                />
                {errors.subject ? (
                  <p className="text-xs text-red-600">{errors.subject}</p>
                ) : (
                  !isEmailChannel && (
                    <p className="text-xs text-gray-500">
                      {t('emailTemplates.fields.subjectNotificationHint')}
                    </p>
                  )
                )}
              </div>
            )}

            {!isEmailChannel && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="notification_message">
                  {t('emailTemplates.fields.notificationMessage')}
                </Label>
                <Textarea
                  id="notification_message"
                  rows={5}
                  value={values.notification_message ?? ''}
                  onChange={(event) =>
                    setNullableField('notification_message', event.target.value)
                  }
                  placeholder={t('emailTemplates.fields.notificationMessagePlaceholder')}
                  {...registerTarget('notification_message', (value) =>
                    setNullableField('notification_message', value)
                  )}
                />
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label>
                {isEmailChannel
                  ? t('emailTemplates.fields.recipients')
                  : t('emailTemplates.fields.notificationRecipients')}
              </Label>
              <MultiSelect
                options={recipientOptions}
                value={
                  isEmailChannel ? values.recipients : values.notification_recipients
                }
                onChange={(next) =>
                  setField(
                    isEmailChannel ? 'recipients' : 'notification_recipients',
                    next
                  )
                }
                placeholder={t('emailTemplates.fields.recipientsPlaceholder')}
                searchPlaceholder={t('common.search')}
                emptyLabel={t('emailTemplates.fields.recipientsEmpty')}
                clearLabel={t('emailTemplates.fields.recipientsClear')}
                removeLabel={t('emailTemplates.fields.recipientsRemove')}
              />
              <p className={cn('text-xs', channelEnabled ? 'text-gray-500' : 'text-amber-600')}>
                {channelEnabled
                  ? t(
                      isEmailChannel
                        ? 'emailTemplates.fields.recipientsEnabledHint'
                        : 'emailTemplates.fields.notificationRecipientsEnabledHint'
                    )
                  : t(
                      isEmailChannel
                        ? 'emailTemplates.fields.recipientsDisabledHint'
                        : 'emailTemplates.fields.notificationRecipientsDisabledHint'
                    )}
              </p>
            </div>

            {isEmailChannel ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mailgun_draft_link">
                  {t('emailTemplates.fields.mailgunDraftLink')}
                </Label>
                <Input
                  id="mailgun_draft_link"
                  value={values.mailgun_draft_link ?? ''}
                  onChange={(event) =>
                    setNullableField('mailgun_draft_link', event.target.value)
                  }
                  placeholder="https://"
                  {...registerTarget('mailgun_draft_link', (value) =>
                    setNullableField('mailgun_draft_link', value)
                  )}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="redirect_url">{t('emailTemplates.fields.redirectUrl')}</Label>
                <Input
                  id="redirect_url"
                  value={values.redirect_url ?? ''}
                  onChange={(event) => setNullableField('redirect_url', event.target.value)}
                  placeholder={t('emailTemplates.fields.redirectUrlPlaceholder')}
                  {...registerTarget('redirect_url', (value) =>
                    setNullableField('redirect_url', value)
                  )}
                />
                <p className="text-xs text-gray-500">
                  {t('emailTemplates.fields.redirectUrlHint')}
                </p>
              </div>
            )}
          </div>

          {/* Colonna variabili */}
          <div className="min-h-0">
            <AvailableVariablesPanel
              variables={values.available_variables}
              onChange={(next) => setField('available_variables', next)}
              onInsert={insertAtCursor}
            />
          </div>
        </div>
      </Modal>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        {/* Sopra la Modal (portale a z-index ≥ 60): overlay e contenuto vanno alzati. */}
        <AlertDialogContent className="z-[130]" overlayClassName="z-[125]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailTemplates.modal.discardTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emailTemplates.modal.discardDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setConfirmCloseOpen(false);
                onClose();
              }}
            >
              {t('emailTemplates.modal.discardConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
